<template>
  <div class="container mt-4 hall-of-fame-page">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <b-button variant="outline-secondary" size="sm" class="back-btn shadow-sm" @click="$router.back()">
        <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回
      </b-button>
      <WxShare :iconOnly="true" title="自在量化荣誉殿堂" desc="感谢所有支持自在量化的朋友们！荣誉殿堂（捐赠墙）" />
    </div>
    <div class="row">
      <div class="col-12 text-center mb-5">
        <router-link to="/hall_of_fame" class="btn btn-outline-warning shadow-sm font-weight-bold">
                  <b-icon icon="trophy" class="mr-1"></b-icon> <h2 class="text-danger font-weight-bold mb-0">荣誉殿堂 (捐赠墙)</h2>
               </router-link>
        <p class="text-muted">感谢所有支持自在量化的朋友们！</p>
      </div>
    </div>

    <!-- Admin Upload Section -->
    <div v-if="isAdmin && showAdminPanel" class="row mb-5 admin-upload-sticky-wrap">
      <div class="col-12">
        <div class="card shadow-sm admin-upload-sticky-card">
          <div class="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
            <strong>管理员上传</strong>
            <div class="d-flex align-items-center">
              <b-form-checkbox v-model="adminViewMode" switch size="lg">
                  {{ adminViewMode ? '管理员视图' : '访客视图预览' }}
              </b-form-checkbox>
              <b-button v-if="adminViewMode" size="sm" variant="outline-primary" class="ml-2" :disabled="generatingThumbs" @click="generateThumbnails">
                {{ generatingThumbs ? '生成中...' : '生成缩略图' }}
              </b-button>
            </div>
          </div>
          <div class="card-body" v-if="adminViewMode">
            <div class="form-group">
              <label>选择截图</label>
              <input type="file" class="form-control-file" ref="fileInput" accept="image/*">
            </div>
            <div class="form-group">
              <label>描述</label>
              <input type="text" class="form-control" v-model="uploadDesc" placeholder="输入描述...">
            </div>
            <div class="form-group">
              <label>日期 (可选)</label>
              <input type="date" class="form-control" v-model="uploadDate">
            </div>
            <button class="btn btn-primary" @click="uploadImage" :disabled="uploading">
              {{ uploading ? '上传中...' : '上传' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Gallery Grid -->
    <transition-group name="gallery-fade" tag="div" class="row" v-if="images.length > 0">
      <div class="col-xl-2 col-lg-3 col-md-4 col-6 mb-4 gallery-col" v-for="(item, index) in images" :key="item.id" :style="{ animationDelay: index * 0.05 + 's' }">
        <div class="card shadow-sm h-100 gallery-card border-0">
          <div class="card-img-wrapper">
             <img ref="galleryImages" :data-id="item.id" :src="getDisplayUrl(item, index)" class="card-img-top" :alt="item.description" @click="showImage(item)" :loading="index < 6 ? 'eager' : 'lazy'" decoding="async" :fetchpriority="index === 0 ? 'high' : 'auto'">
          </div>
          <div class="card-body p-2" v-if="isAdmin && adminViewMode">
             <button class="btn btn-sm btn-danger btn-block" @click="deleteImage(item.id)">删除</button>
          </div>
        </div>
      </div>
    </transition-group>
    <div v-if="images.length > 0">
      <div ref="pageSentinel" style="height: 1px;"></div>
      <div v-if="loadingMore" class="text-center text-muted py-2">加载中...</div>
    </div>
    
    <div v-else class="text-center text-muted py-5">
        暂无数据
    </div>

    <!-- Admin Floating Button -->
    <div v-if="isAdmin" class="admin-float-btn" @click="showAdminPanel = !showAdminPanel" :title="showAdminPanel ? '收起管理面板' : '展开管理面板'">
      <b-icon :icon="showAdminPanel ? 'x-circle' : 'tools'"></b-icon>
      <span class="ml-1">{{ showAdminPanel ? '收起' : '管理' }}</span>
    </div>

    <!-- Modal for Image View -->
    <b-modal id="image-modal" size="xl" hide-footer title="查看大图" centered>
      <img :src="currentImage" class="img-fluid d-block mx-auto" alt="大图">
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request'
import WxShare from '@/components/share/WxShare.vue'

export default {
  name: 'HallOfFame',
  components: {
    WxShare
  },
  data() {
    return {
      images: [],
      isAdmin: false,
      adminViewMode: false,
      showAdminPanel: false,
      uploadDesc: '',
      uploadDate: '', // New data property for date
      uploading: false,
      currentImage: '',
      page: 1,
      pageSize: 24,
      hasMore: true,
      loadingList: false,
      loadingMore: false,
      pageObserver: null,
      generatingThumbs: false,
      visibleImageIds: {},
      observer: null,
      placeholderImage: 'data:image/gif;base64,R0lGODlhAQABAAAAACw=',
      preconnectAdded: false
    }
  },
  created() {
    this.checkAdmin()
    this.fetchImages(true)
  },
  mounted() {
    this.ensurePreconnect()
    this.initObserver()
    this.initPageObserver()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.pageObserver) {
      this.pageObserver.disconnect()
      this.pageObserver = null
    }
  },
  methods: {
    ensurePreconnect() {
      if (this.preconnectAdded) return
      this.preconnectAdded = true
      const preconnect = document.createElement('link')
      preconnect.rel = 'preconnect'
      preconnect.href = 'https://api.zizizaizai.com'
      preconnect.crossOrigin = ''
      document.head.appendChild(preconnect)
      const dns = document.createElement('link')
      dns.rel = 'dns-prefetch'
      dns.href = '//api.zizizaizai.com'
      document.head.appendChild(dns)
    },
    checkAdmin() {
      this.isAdmin = false
      const token = localStorage.getItem('token')
      if (token) {
        try {
          const base64Url = token.split('.')[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join(''))
          const decoded = JSON.parse(jsonPayload)
          const roles = decoded.roles || ''
          // Strict check for admin role
          if (roles && roles.includes('admin')) {
            this.isAdmin = true
          }
        } catch (e) {
          console.error('Token decode error', e)
          this.isAdmin = false
        }
      }
    },
    fetchImages(reset) {
      if (reset) {
        this.page = 1
        this.hasMore = true
        this.images = []
        this.visibleImageIds = {}
      }
      if (!this.hasMore) return
      if (this.loadingList || this.loadingMore) return
      const loadingKey = reset ? 'loadingList' : 'loadingMore'
      this[loadingKey] = true
      request_zzquant({
        url: '/hall_of_fame/list',
        method: 'get',
        params: {
          page: this.page,
          page_size: this.pageSize
        }
      }).then(res => {
        if (res.data && res.data.code === 20000) {
          const payload = res.data.data
          const list = Array.isArray(payload) ? payload : (payload && payload.list ? payload.list : [])
          if (reset) {
            this.images = list
          } else {
            this.images = this.images.concat(list)
          }
          if (payload && typeof payload.total === 'number') {
            this.hasMore = this.images.length < payload.total
          } else {
            this.hasMore = list.length === this.pageSize
          }
          if (list.length > 0) {
            this.page += 1
          }
          this.$nextTick(() => {
            this.initObserver()
            this.initPageObserver()
          })
        }
      }).finally(() => {
        this[loadingKey] = false
      })
    },
    getImageUrl(path) {
        if (!path) return ''
        // If path starts with http, use it, else prepend baseURL
        if (path.startsWith('http')) return path
        // Hardcode or get from config. 
        // Note: request_zzquant has baseURL https://api.zizizaizai.com
        return `https://api.zizizaizai.com${path}`
    },
    formatDate(dateStr) {
        if (!dateStr) return ''
        return dateStr.split(' ')[0]
    },
    initObserver() {
      if (this.observer) {
        this.observer.disconnect()
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const id = entry.target && entry.target.dataset ? entry.target.dataset.id : null
          if (id) {
            this.$set(this.visibleImageIds, id, true)
          }
          this.observer.unobserve(entry.target)
        })
      }, { rootMargin: '200px 0px' })
      const nodes = this.$refs.galleryImages || []
      const list = Array.isArray(nodes) ? nodes : [nodes]
      list.forEach((node) => {
        if (node && node.dataset && node.dataset.id) {
          this.observer.observe(node)
        }
      })
    },
    initPageObserver() {
      if (this.pageObserver) {
        this.pageObserver.disconnect()
      }
      const sentinel = this.$refs.pageSentinel
      if (!sentinel) return
      this.pageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          if (this.hasMore && !this.loadingMore) {
            this.fetchImages(false)
          }
        })
      }, { rootMargin: '200px 0px' })
      this.pageObserver.observe(sentinel)
    },
    getThumbUrl(item) {
      if (!item) return ''
      if (item.thumb_url) {
        return this.getImageUrl(item.thumb_url)
      }
      const original = this.getImageUrl(item.image_url)
      if (original && !original.includes('?')) {
        return original + '?x-oss-process=image/resize,m_lfit,w_360,h_360'
      }
      return original
    },
    getDisplayUrl(item, index) {
      if (!item || !item.id) return this.placeholderImage
      if (index < 6) return this.getThumbUrl(item)
      if (this.visibleImageIds[item.id]) return this.getThumbUrl(item)
      return this.placeholderImage
    },
    generateThumbnails() {
      if (this.generatingThumbs) return
      this.generatingThumbs = true
      request_zzquant({
        url: '/hall_of_fame/thumbnail/generate',
        method: 'post'
      }).then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast('缩略图生成完成', { variant: 'success', solid: true })
          this.fetchImages(true)
        } else {
          this.$bvToast.toast(res.data.message || '生成失败', { variant: 'danger', solid: true })
        }
      }).catch(() => {
        this.$bvToast.toast('生成出错', { variant: 'danger', solid: true })
      }).finally(() => {
        this.generatingThumbs = false
      })
    },
    uploadImage() {
      const file = this.$refs.fileInput.files[0]
      if (!file) {
        this.$bvToast.toast('请选择文件', { variant: 'warning', solid: true })
        return
      }
      
      this.uploading = true
      const formData = new FormData()
      formData.append('file', file)
      formData.append('description', this.uploadDesc)
      if (this.uploadDate) {
        formData.append('created_time', this.uploadDate)
      }
      
      request_zzquant({
        url: '/hall_of_fame/upload',
        method: 'post',
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
      }).then(res => {
        this.uploading = false
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast('上传成功', { variant: 'success', solid: true })
          this.uploadDesc = ''
          this.uploadDate = ''
          this.$refs.fileInput.value = ''
          this.fetchImages(true)
        } else {
          this.$bvToast.toast(res.data.message || '上传失败', { variant: 'danger', solid: true })
        }
      }).catch(() => {
        this.uploading = false
        this.$bvToast.toast('上传出错', { variant: 'danger', solid: true })
      })
    },
    async deleteImage(id) {
      const ok = await this.$bvModal.msgBoxConfirm('确定要删除这张图片吗？', {
        title: '确认删除',
        okVariant: 'danger',
        okTitle: '确定',
        cancelTitle: '取消',
        centered: true
      })
      if (!ok) return
      
      request_zzquant({
        url: `/hall_of_fame/delete/${id}`,
        method: 'post'
      }).then(res => {
        if (res.data && res.data.code === 20000) {
          this.$bvToast.toast('删除成功', { variant: 'success', solid: true })
          // Filter out the deleted image from the local list
          this.images = this.images.filter(item => item.id !== id)
          // Re-initialize intersection observers for lazy loading
          this.$nextTick(() => {
            this.initObserver()
            this.initPageObserver()
          })
        } else {
          this.$bvToast.toast(res.data.message || '删除失败', { variant: 'danger', solid: true })
        }
      })
    },
    showImage(item) {
        this.currentImage = this.getImageUrl(item.image_url)
        this.$bvModal.show('image-modal')
    }
  }
}
</script>

<style scoped>
.gallery-col {
    opacity: 0;
    animation: fadeInUp 0.5s ease-out forwards;
}

.admin-upload-sticky-wrap {
    position: sticky;
    top: 10px;
    z-index: 30;
}

.admin-upload-sticky-card {
    border: 1px solid rgba(255, 193, 7, 0.35);
    backdrop-filter: saturate(120%) blur(4px);
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.gallery-card {
    transition: all 0.3s ease;
    background: transparent;
}

.gallery-card:hover {
    transform: translateY(-5px);
    z-index: 10;
}

.card-img-wrapper {
    height: 160px; /* Slightly smaller for denser grid */
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
    transition: all 0.3s ease;
    cursor: pointer;
}

.gallery-card:hover .card-img-wrapper {
    box-shadow: 0 8px 15px rgba(0,0,0,0.15);
    transform: scale(1.02);
}

.card-img-top {
    max-height: 100%;
    width: auto;
    max-width: 100%;
    transition: transform 0.5s ease;
}

.gallery-card:hover .card-img-top {
    transform: scale(1.1);
}

@media (max-width: 768px) {
    .admin-upload-sticky-wrap {
        top: 6px;
    }
}

.admin-float-btn {
    position: fixed;
    right: 25px;
    bottom: 80px;
    z-index: 1000;
    background-color: #ffc107;
    color: #1e293b;
    border-radius: 50px;
    padding: 10px 18px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    cursor: pointer;
    font-weight: bold;
    font-size: 14px;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
}

.admin-float-btn:hover {
    transform: scale(1.08) translateY(-2px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.25);
    background-color: #ffca2c;
}

body.theme-dark .admin-float-btn {
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
    color: #0f172a;
}

/* Gallery transitions */
.gallery-fade-enter, .gallery-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}
.gallery-fade-leave-active {
    transition: all 0.3s ease;
}
</style>
